/**
 * @file Vuex Store for drag and drop events of files/texts/links over the Padlet page
 */

import type { PostModalDropZone } from 'types'
import { Module } from 'vuex'
import { RootState } from 'vuexstore/surface/types'

enum DraggedDataType {
  File,
  Link,
  Text,
}

interface DragAndDropEventsState {
  isHandlingDragEvent: boolean
  draggedDataType: DraggedDataType
  dropActionMessage: string
  // when a post modal is docked, anything dropped inside it will be added to the draft and things dropped outside will create new post(s)
  // => store the bounds of docked post modal here so we can know whether something is dropped inside or outside
  postModalDropZone: PostModalDropZone | null
}

const initialState = (): DragAndDropEventsState => {
  return {
    isHandlingDragEvent: false,
    draggedDataType: DraggedDataType.File,
    dropActionMessage: '',
    postModalDropZone: null,
  }
}

const dragAndDropEvents: Module<DragAndDropEventsState, RootState> = {
  namespaced: true,
  state: initialState(),
  getters: {
    isHandlingDragEvent: (state): boolean => state.isHandlingDragEvent,
    draggedDataType: (state): DraggedDataType => state.draggedDataType,
    dropActionMessage: (state): string => state.dropActionMessage,
    postModalDropZone: (state): PostModalDropZone | null => state.postModalDropZone,
  },
  mutations: {
    START_HANDLING_DRAG_EVENT(state): void {
      state.isHandlingDragEvent = true
    },
    STOP_HANDLING_DRAG_EVENT(state): void {
      state.isHandlingDragEvent = false
    },
    SET_DRAGGED_DATA_TYPE(state, { newType }: { newType: DraggedDataType }): void {
      state.draggedDataType = newType
    },
    SET_DROP_ACTION_MESSAGE(state, { message }: { message: string }): void {
      state.dropActionMessage = message
    },
    SET_POST_MODAL_DROP_ZONE(state, dropZone): void {
      state.postModalDropZone = { ...dropZone }
    },
    RESET_POST_MODAL_DROP_ZONE(state): void {
      state.postModalDropZone = null
    },
  },
  actions: {
    startHandlingDragEvent({ commit }): void {
      commit('START_HANDLING_DRAG_EVENT')
    },
    stopHandlingDragEvent({ commit }): void {
      commit('STOP_HANDLING_DRAG_EVENT')
    },
    setDraggedDataType({ commit }, { newType }: { newType: DraggedDataType }): void {
      commit('SET_DRAGGED_DATA_TYPE', { newType })
    },
    setDropActionMessage({ commit }, { message }: { message: string }): void {
      commit('SET_DROP_ACTION_MESSAGE', { message })
    },
    setPostModalDropZone({ commit }, newDropZone: PostModalDropZone): void {
      commit('SET_POST_MODAL_DROP_ZONE', newDropZone)
    },
    resetPostModalDropZone({ commit }): void {
      commit('RESET_POST_MODAL_DROP_ZONE')
    },
  },
}

export default dragAndDropEvents
export { DraggedDataType, DragAndDropEventsState }
